import { useQuery } from '@tanstack/react-query';

import { useMemo, useState } from 'react';

import { ContentCopy, Edit, PersonAdd, Refresh } from '@mui/icons-material';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';

import { useApiClient, useCopyToClipboard, useIsMobile, usePersistedTablePagination, useToastService } from '@@common';
import { GalleryHeader, GalleryStatistics } from '@@components';

import { TenantUserModel, TenantUserModelPagingResultModel, UserClient } from '../../api/api';
import InviteUserDialog from '../Profile/InviteUserDialog';

const pageSizeOptions = [20, 50, 100];

export default function UsersListView() {
	const client = useApiClient(UserClient);
	const isMobile = useIsMobile();
	const toastService = useToastService();
	const copyToClipboard = useCopyToClipboard();
	const { paginationModel, setPaginationModel } = usePersistedTablePagination('users.pageSize', pageSizeOptions[1]);
	const [editingUser, setEditingUser] = useState<TenantUserModel | null>(null);
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

	const { data, isLoading, isError, refetch } = useQuery<TenantUserModelPagingResultModel>({
		queryKey: ['users', paginationModel.page, paginationModel.pageSize],
		queryFn: () => client.getUsers(paginationModel.page, paginationModel.pageSize, undefined),
	});

	const rows = data?.items ?? [];
	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize : rows.length) : 0),
		[data, paginationModel, rows.length],
	);

	const columns = useMemo<GridColDef<TenantUserModel>[]>(
		() => [
			{
				field: 'email',
				headerName: 'Email',
				flex: 1.4,
				minWidth: 220,
			},
			{
				field: 'displayName',
				headerName: 'Display name',
				flex: 1,
				minWidth: 180,
			},
			{
				field: 'givenName',
				headerName: 'Given name',
				flex: 0.8,
				minWidth: 140,
			},
			{
				field: 'surname',
				headerName: 'Surname',
				flex: 0.8,
				minWidth: 140,
			},
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Actions',
				width: 72,
				getActions: (params) => [
					<GridActionsCellItem icon={<Edit />} label="Edit user" onClick={() => setEditingUser(params.row)} showInMenu />,
					<GridActionsCellItem icon={<ContentCopy />} label="Copy ID" onClick={() => copyToClipboard(params.row.id, String)} showInMenu />,
				],
			},
		],
		[copyToClipboard],
	);

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minHeight: 0, minWidth: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title="Users"
				subtitle="Browse users in the selected tenant."
				utilityActions={
					<Tooltip title="Refresh users">
						<IconButton onClick={() => refetch()}>
							<Refresh />
						</IconButton>
					</Tooltip>
				}
				statistics={<GalleryStatistics items={[{ label: 'Users on this page', value: rows.length }]} />}
				primaryAction={<Button variant="contained" startIcon={<PersonAdd />} onClick={() => setIsInviteDialogOpen(true)}>Invite user</Button>}
			/>

			{isError && <Alert severity="error">Users could not be loaded. Please refresh and try again.</Alert>}

			<Paper sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
				<DataGrid
					loading={isLoading}
					rows={rows}
					columns={columns}
					getRowId={(row) => row.id}
					rowCount={rowCount}
					paginationMode="server"
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					disableColumnFilter
					disableColumnMenu
					density={isMobile ? 'compact' : 'standard'}
					columnVisibilityModel={{ givenName: !isMobile, surname: !isMobile }}
					sx={{ border: 0, minWidth: 0 }}
				/>
			</Paper>
			{editingUser && (
				<EditUserDialog
					user={editingUser}
					client={client}
					onClose={() => setEditingUser(null)}
					onUpdated={() => {
						toastService.success('User updated');
						refetch();
					}}
				/>
			)}
			{isInviteDialogOpen && <InviteUserDialog onClose={() => setIsInviteDialogOpen(false)} />}
		</Stack>
	);
}

function EditUserDialog({
	user,
	client,
	onClose,
	onUpdated,
}: {
	user: TenantUserModel;
	client: UserClient;
	onClose: () => void;
	onUpdated: () => void;
}) {
	const [displayName, setDisplayName] = useState(user.displayName ?? '');
	const [givenName, setGivenName] = useState(user.givenName ?? '');
	const [surname, setSurname] = useState(user.surname ?? '');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const isValid = Boolean(displayName.trim()) && Boolean(givenName.trim()) && Boolean(surname.trim());

	const handleSave = async () => {
		if (!isValid) {
			return;
		}

		setSaving(true);
		setError(null);
		try {
			await client.updateUser(user.id, {
				displayName: displayName.trim(),
				givenName: givenName.trim(),
				surname: surname.trim(),
			});
			onUpdated();
			onClose();
		} catch (updateError) {
			setError(updateError instanceof Error ? updateError.message : 'The user could not be updated.');
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit user</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					{error && <Alert severity="error">{error}</Alert>}
					<TextField label="Email" value={user.email} disabled fullWidth />
					<TextField label="Display name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required autoFocus fullWidth />
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
						<TextField label="First name" value={givenName} onChange={(event) => setGivenName(event.target.value)} required fullWidth />
						<TextField label="Last name" value={surname} onChange={(event) => setSurname(event.target.value)} required fullWidth />
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave} disabled={saving || !isValid} loading={saving}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
